/**
 * App context: who am I, which company, my role, the local db, the sync
 * engine and the repos. Everything below the auth/onboarding gate can assume
 * `company` is set.
 */
import {
  Autosaver,
  RouteDb,
  FakeRemote,
  SupabaseRemote,
  SyncEngine,
  getDb,
  kvGet,
  kvSet,
  requestPersistentStorage,
  isAdminRole,
  isManagerRole,
  CompanyRepo,
  nowIso,
  newId,
  type Company,
  type MaintenanceRequestRow,
  type TimeoffRequestRow,
  type DriverDocumentRow,
  type UniformRequestRow,
  type DriverProfileRow,
  type TruckFavoriteRow,
  type AccidentReportRow,
  type AccidentPhotoRow,
  type Op201FormRow,
  type ShipperRow,
  type ShipperContactRow,
  type BulkPickupRow,
  type HandbookAckRow,
  type ConsentSignatureRow,
  type ResourcePageRow,
  type WorkOrderRow,
  type CalendarEventRow,
  type RouteRow,
  type ScheduleAssignmentRow,
  type ScheduleDayRow,
  type ScheduleAvailabilityRow,
  type ScheduleExcludedRow,
  type LineupLineRow,
  type LineupSectionRow,
  type LineupSpotRow,
  type LineupAssignmentRow,
  type UniformItemRow,
  type VehicleDocumentRow,
  type HandbookVersionRow,
  type ConsentVersionRow,
  type BroadcastRow,
  type BroadcastReadRow,
  type PartRow,
  type OdometerReadingRow,
  type RecurringTaskRow,
  type RecurringTaskTruckRow,
  type MemberTerminalRow,
  type TerminalRow,
  type TruckRow,
  type RemoteApi,
  type Role,
  type SyncStatus,
  type CompanySettings,
} from '@routeback/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { HAS_CLOUD, supabase } from './supabase';
import { uploadPending } from './photos';
import { defaultResourcePages } from '../features/resources/resources';
import { onPushedKick } from './emailKick';

export interface CompanyInfo {
  id: string;
  name: string;
  role: Role;
}

/** The only way screens write synced tables (offline-first: Dexie + outbox). Added per feature. */
export interface Repos {
  terminals: CompanyRepo<TerminalRow>;
  memberTerminals: CompanyRepo<MemberTerminalRow>;
  trucks: CompanyRepo<TruckRow>;
  maintenanceRequests: CompanyRepo<MaintenanceRequestRow>;
  timeoffRequests: CompanyRepo<TimeoffRequestRow>;
  driverDocuments: CompanyRepo<DriverDocumentRow>;
  uniformRequests: CompanyRepo<UniformRequestRow>;
  driverProfiles: CompanyRepo<DriverProfileRow>;
  truckFavorites: CompanyRepo<TruckFavoriteRow>;
  accidentReports: CompanyRepo<AccidentReportRow>;
  accidentPhotos: CompanyRepo<AccidentPhotoRow>;
  op201Forms: CompanyRepo<Op201FormRow>;
  shippers: CompanyRepo<ShipperRow>;
  shipperContacts: CompanyRepo<ShipperContactRow>;
  bulkPickups: CompanyRepo<BulkPickupRow>;
  handbookAcks: CompanyRepo<HandbookAckRow>;
  consentSignatures: CompanyRepo<ConsentSignatureRow>;
  resourcePages: CompanyRepo<ResourcePageRow>;
  workOrders: CompanyRepo<WorkOrderRow>;
  parts: CompanyRepo<PartRow>;
  odometerReadings: CompanyRepo<OdometerReadingRow>;
  recurringTasks: CompanyRepo<RecurringTaskRow>;
  recurringTaskTrucks: CompanyRepo<RecurringTaskTruckRow>;
  calendarEvents: CompanyRepo<CalendarEventRow>;
  routes: CompanyRepo<RouteRow>;
  scheduleAssignments: CompanyRepo<ScheduleAssignmentRow>;
  scheduleDays: CompanyRepo<ScheduleDayRow>;
  scheduleAvailability: CompanyRepo<ScheduleAvailabilityRow>;
  scheduleExcluded: CompanyRepo<ScheduleExcludedRow>;
  lineupLines: CompanyRepo<LineupLineRow>;
  lineupSections: CompanyRepo<LineupSectionRow>;
  lineupSpots: CompanyRepo<LineupSpotRow>;
  lineupAssignments: CompanyRepo<LineupAssignmentRow>;
  uniformItems: CompanyRepo<UniformItemRow>;
  vehicleDocuments: CompanyRepo<VehicleDocumentRow>;
  handbookVersions: CompanyRepo<HandbookVersionRow>;
  consentVersions: CompanyRepo<ConsentVersionRow>;
  broadcasts: CompanyRepo<BroadcastRow>;
  broadcastReads: CompanyRepo<BroadcastReadRow>;
}

/** The company row (name, timezone, billing state, features). */
export type CompanyDetails = Pick<Company, 'id' | 'name' | 'timezone' | 'fedex_csa_name' | 'brand_color' | 'logo_path' | 'subscription_status' | 'trial_ends_at' | 'features' | 'lineup_public_token'> & { slug?: string };

/** 'all' or a terminal id. */
export type TerminalChoice = 'all' | string;

export interface AppState {
  mode: 'cloud' | 'local';
  userId: string;
  userName: string;
  email: string;
  company: CompanyInfo;
  role: Role;
  isManager: boolean;
  isAdmin: boolean;
  db: RouteDb;
  engine: SyncEngine;
  repos: Repos;
  settings: CompanySettings;
  saveSettings: (patch: Partial<CompanySettings>) => Promise<void>;
  details: CompanyDetails;
  saveDetails: (patch: Partial<Pick<CompanyDetails, 'name' | 'timezone' | 'fedex_csa_name' | 'brand_color'>>) => Promise<string | null>;
  /** Paid up / on trial / comped. When false the app is read-only. */
  active: boolean;
  /** Live, active terminals (sorted). */
  terminals: TerminalRow[];
  /** Show terminal pickers and labels only for multi-terminal companies. */
  multiTerminal: boolean;
  terminal: TerminalChoice;
  setTerminal: (t: TerminalChoice) => void;
  sync: () => Promise<void>;
  syncStatus: SyncStatus;
  signOut: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);
export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

export const LOCAL_USER = '00000000-0000-4000-8000-000000000001';
export const LOCAL_COMPANY = '00000000-0000-4000-8000-000000000002';

// Singletons that live for the page lifetime.
let _remote: RemoteApi | null = null;
function remote(): RemoteApi {
  if (!_remote) _remote = supabase ? new SupabaseRemote(supabase) : new FakeRemote();
  return _remote;
}
let _engine: SyncEngine | null = null;
function engine(onAuth: () => void): SyncEngine {
  if (!_engine) _engine = new SyncEngine({ db: getDb(), remote: remote(), onAuthRequired: onAuth, log: (m, x) => console.warn('[sync]', m, x), onPushed: onPushedKick });
  return _engine;
}

export interface Identity {
  userId: string;
  userName: string;
  email: string;
  company: CompanyInfo;
}

export function AppProvider({ identity, onAuthRequired, onSignOut, onSwitchCompany, children }: {
  identity: Identity;
  onAuthRequired: () => void;
  onSignOut: () => Promise<void>;
  onSwitchCompany: (id: string) => Promise<void>;
  children: ReactNode;
}) {
  const db = getDb();
  const eng = useMemo(() => engine(onAuthRequired), [onAuthRequired]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(eng.getStatus());
  const [settings, setSettings] = useState<CompanySettings>({});
  const companyId = identity.company.id;

  const repos = useMemo<Repos>(() => {
    const ctx = { db, engine: eng, companyId, userId: identity.userId };
    return {
      terminals: new CompanyRepo<TerminalRow>(ctx, 'terminals'),
      memberTerminals: new CompanyRepo<MemberTerminalRow>(ctx, 'member_terminals'),
      trucks: new CompanyRepo<TruckRow>(ctx, 'trucks'),
      maintenanceRequests: new CompanyRepo<MaintenanceRequestRow>(ctx, 'maintenance_requests'),
      timeoffRequests: new CompanyRepo<TimeoffRequestRow>(ctx, 'timeoff_requests'),
      driverDocuments: new CompanyRepo<DriverDocumentRow>(ctx, 'driver_documents'),
      uniformRequests: new CompanyRepo<UniformRequestRow>(ctx, 'uniform_requests'),
      driverProfiles: new CompanyRepo<DriverProfileRow>(ctx, 'driver_profiles'),
      truckFavorites: new CompanyRepo<TruckFavoriteRow>(ctx, 'truck_favorites'),
      accidentReports: new CompanyRepo<AccidentReportRow>(ctx, 'accident_reports'),
      accidentPhotos: new CompanyRepo<AccidentPhotoRow>(ctx, 'accident_photos'),
      op201Forms: new CompanyRepo<Op201FormRow>(ctx, 'op201_forms'),
      shippers: new CompanyRepo<ShipperRow>(ctx, 'shippers'),
      shipperContacts: new CompanyRepo<ShipperContactRow>(ctx, 'shipper_contacts'),
      bulkPickups: new CompanyRepo<BulkPickupRow>(ctx, 'bulk_pickups'),
      handbookAcks: new CompanyRepo<HandbookAckRow>(ctx, 'handbook_acks'),
      consentSignatures: new CompanyRepo<ConsentSignatureRow>(ctx, 'consent_signatures'),
      resourcePages: new CompanyRepo<ResourcePageRow>(ctx, 'resource_pages'),
      workOrders: new CompanyRepo<WorkOrderRow>(ctx, 'work_orders'),
      parts: new CompanyRepo<PartRow>(ctx, 'parts'),
      odometerReadings: new CompanyRepo<OdometerReadingRow>(ctx, 'odometer_readings'),
      recurringTasks: new CompanyRepo<RecurringTaskRow>(ctx, 'recurring_tasks'),
      recurringTaskTrucks: new CompanyRepo<RecurringTaskTruckRow>(ctx, 'recurring_task_trucks'),
      calendarEvents: new CompanyRepo<CalendarEventRow>(ctx, 'calendar_events'),
      routes: new CompanyRepo<RouteRow>(ctx, 'routes'),
      scheduleAssignments: new CompanyRepo<ScheduleAssignmentRow>(ctx, 'schedule_assignments'),
      scheduleDays: new CompanyRepo<ScheduleDayRow>(ctx, 'schedule_days'),
      scheduleAvailability: new CompanyRepo<ScheduleAvailabilityRow>(ctx, 'schedule_availability'),
      scheduleExcluded: new CompanyRepo<ScheduleExcludedRow>(ctx, 'schedule_excluded'),
      lineupLines: new CompanyRepo<LineupLineRow>(ctx, 'lineup_lines'),
      lineupSections: new CompanyRepo<LineupSectionRow>(ctx, 'lineup_sections'),
      lineupSpots: new CompanyRepo<LineupSpotRow>(ctx, 'lineup_spots'),
      lineupAssignments: new CompanyRepo<LineupAssignmentRow>(ctx, 'lineup_assignments'),
      uniformItems: new CompanyRepo<UniformItemRow>(ctx, 'uniform_items'),
      vehicleDocuments: new CompanyRepo<VehicleDocumentRow>(ctx, 'vehicle_documents'),
      handbookVersions: new CompanyRepo<HandbookVersionRow>(ctx, 'handbook_versions'),
      consentVersions: new CompanyRepo<ConsentVersionRow>(ctx, 'consent_versions'),
      broadcasts: new CompanyRepo<BroadcastRow>(ctx, 'broadcasts'),
      broadcastReads: new CompanyRepo<BroadcastReadRow>(ctx, 'broadcast_reads'),
    };
  }, [db, eng, companyId, identity.userId]);

  // ── company details: cached in kv, source of truth is the companies row ──
  const detailsKey = `company:${companyId}`;
  const [details, setDetails] = useState<CompanyDetails>({
    id: companyId, name: identity.company.name, timezone: 'America/Chicago', fedex_csa_name: '', brand_color: null,
    logo_path: null, subscription_status: 'trialing', trial_ends_at: null, features: {}, lineup_public_token: '',
  });
  useEffect(() => {
    let alive = true;
    (async () => {
      const cached = await kvGet<CompanyDetails>(detailsKey);
      if (alive && cached) setDetails(cached);
      if (supabase) {
        const { data } = await supabase
          .from('companies')
          .select('id,name,slug,timezone,fedex_csa_name,brand_color,logo_path,subscription_status,trial_ends_at,features,lineup_public_token')
          .eq('id', companyId)
          .maybeSingle();
        if (alive && data) {
          setDetails(data as CompanyDetails);
          await kvSet(detailsKey, data);
        }
      }
    })();
    return () => { alive = false; };
  }, [companyId, detailsKey]);
  const saveDetails = useCallback(async (patch: Partial<Pick<CompanyDetails, 'name' | 'timezone' | 'fedex_csa_name' | 'brand_color'>>) => {
    if (supabase) {
      const { error } = await supabase.from('companies').update(patch).eq('id', companyId);
      if (error) return error.message;
    }
    setDetails((d) => {
      const next = { ...d, ...patch };
      void kvSet(detailsKey, next);
      return next;
    });
    return null;
  }, [companyId, detailsKey]);
  const active = details.subscription_status === 'active' || details.subscription_status === 'grace' || details.subscription_status === 'comped'
    || (details.subscription_status === 'trialing' && (!details.trial_ends_at || new Date(details.trial_ends_at).getTime() > Date.now()));

  // ── terminals: live from the local store; the picker only matters with 2+ ──
  const terminals = useLiveQuery(
    async () => (await db.table<TerminalRow, string>('terminals').where('company_id').equals(companyId).toArray())
      .filter((t) => !t.deleted_at && t.active)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [db, companyId],
    [] as TerminalRow[],
  );
  const myTerminals = useLiveQuery(
    async () => (await db.table<MemberTerminalRow, string>('member_terminals').where('company_id').equals(companyId).toArray())
      .filter((m) => !m.deleted_at && m.user_id === identity.userId),
    [db, companyId, identity.userId],
    [] as MemberTerminalRow[],
  );
  const terminalKey = `terminal:${companyId}`;
  const [terminal, setTerminalState] = useState<TerminalChoice>('all');
  const [terminalLoaded, setTerminalLoaded] = useState(false);
  useEffect(() => {
    void kvGet<TerminalChoice>(terminalKey).then((t) => {
      if (t) setTerminalState(t);
      setTerminalLoaded(true);
    });
  }, [terminalKey]);
  // First run: open on the person's home terminal; drop a choice that no longer exists.
  useEffect(() => {
    if (!terminalLoaded) return;
    if (terminal !== 'all' && terminals.length && !terminals.some((t) => t.id === terminal)) setTerminalState('all');
  }, [terminalLoaded, terminal, terminals]);
  useEffect(() => {
    if (!terminalLoaded) return;
    void kvGet<TerminalChoice>(terminalKey).then((saved) => {
      if (saved) return;
      const home = myTerminals.find((m) => m.is_home);
      if (home) setTerminalState(home.terminal_id);
    });
  }, [terminalLoaded, terminalKey, myTerminals]);
  const setTerminal = useCallback((t: TerminalChoice) => {
    setTerminalState(t);
    void kvSet(terminalKey, t);
  }, [terminalKey]);

  // Local mode (no backend): the server would have created the first terminal
  // and the setup-wizard flag; do the same on this device once.
  useEffect(() => {
    if (supabase) return;
    void (async () => {
      // Default document types (server: private.seed_company).
      if ((await db.table('document_types').where('company_id').equals(companyId).count()) === 0) {
        const now = nowIso();
        const base = { company_id: companyId, created_by: identity.userId, created_at: now, updated_at: now, deleted_at: null, requires_expiry: true, alert_days: 30, active: true };
        await db.table('document_types').bulkPut([
          { ...base, id: newId(), code: 'license', label: "Driver's license", sort_order: 1, driver_uploads: true },
          { ...base, id: newId(), code: 'med_card', label: 'Medical card', sort_order: 2, driver_uploads: true },
          { ...base, id: newId(), code: 'safety_training', label: 'Safety training', sort_order: 3, driver_uploads: false },
        ]);
      }
      // Default uniform items (server: private.seed_uniform_items).
      if ((await db.table('uniform_items').where('company_id').equals(companyId).count()) === 0) {
        const now = nowIso();
        const base = { company_id: companyId, created_by: identity.userId, created_at: now, updated_at: now, deleted_at: null, active: true };
        const std = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
        await db.table('uniform_items').bulkPut([
          ['Long sleeve shirt', std, 3, ''], ['Short sleeve shirt', std, 3, ''], ['Fleece pullover', std, 1, ''],
          ['Vest', std, 1, ''], ['Shorts', [], 2, 'e.g. 32 Regular'], ['Pants', [], 2, 'e.g. 32×30'],
        ].map(([name, sizes, max_qty, size_hint], i) => ({ ...base, id: newId(), name, sizes, max_qty, size_hint, sort_order: i + 1 })));
      }
      // Default resource pages (server: private.seed_resource_pages).
      if ((await db.table('resource_pages').where('company_id').equals(companyId).count()) === 0) {
        const now = nowIso();
        const base = { company_id: companyId, created_by: identity.userId, created_at: now, updated_at: now, deleted_at: null };
        await db.table('resource_pages').bulkPut(defaultResourcePages().map((p) => ({ ...base, id: newId(), ...p })));
      }
      const done = await kvGet<boolean>(`local-seeded:${companyId}`);
      if (done) return;
      const n = await db.table('terminals').where('company_id').equals(companyId).count();
      if (n === 0) {
        const t = await repos.terminals.save(repos.terminals.blank({ name: 'Main terminal', code: '', address: '', timezone: null, active: true }));
        await repos.memberTerminals.save(repos.memberTerminals.blank({ user_id: identity.userId, terminal_id: t.id, is_home: true }));
      }
      const cur = (await kvGet<CompanySettings>(`settings:${companyId}`)) ?? {};
      if (!('onboarding_done' in cur)) {
        await kvSet(`settings:${companyId}`, { ...cur, onboarding_done: false });
        setSettings((sNow) => ({ ...sNow, onboarding_done: false }));
      }
      await kvSet(`local-seeded:${companyId}`, nowIso());
    })();
  }, [db, companyId, repos, identity.userId]);

  const sync = useCallback(() => eng.sync(companyId), [eng, companyId]);

  // ── settings: cached in kv, source of truth is company_settings in the cloud ──
  const settingsKey = `settings:${companyId}`;
  useEffect(() => {
    let alive = true;
    (async () => {
      const cached = (await kvGet<CompanySettings>(settingsKey)) ?? {};
      if (alive) setSettings(cached);
      if (supabase) {
        const { data } = await supabase.from('company_settings').select('key,value').eq('company_id', companyId);
        if (alive && data) {
          const next = Object.fromEntries(data.map((r) => [r.key as string, r.value])) as CompanySettings;
          setSettings(next);
          await kvSet(settingsKey, next);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId, settingsKey]);

  const saveSettings = useCallback(
    async (patch: Partial<CompanySettings>) => {
      const next = { ...settings, ...patch };
      setSettings(next);
      await kvSet(settingsKey, next);
      if (supabase) {
        const rows = Object.entries(patch).map(([key, value]) => ({ company_id: companyId, key, value }));
        const { error } = await supabase.from('company_settings').upsert(rows, { onConflict: 'company_id,key' });
        if (error) console.warn('[settings] not saved to cloud', error.message);
      }
    },
    [settings, settingsKey, companyId],
  );

  // ── sync triggers: foreground, network regain, every 30 s, status feed ──
  useEffect(() => eng.subscribe(setSyncStatus), [eng]);
  useEffect(() => {
    void requestPersistentStorage();
    const syncAll = () => { void sync().finally(() => void uploadPending()); };
    syncAll();
    const iv = setInterval(syncAll, 30_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') syncAll();
    };
    const onOnline = () => {
      eng.setOnline(true);
      syncAll();
    };
    const onOffline = () => eng.setOnline(false);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [eng, sync]);

  const value: AppState = {
    mode: HAS_CLOUD ? 'cloud' : 'local',
    userId: identity.userId,
    userName: identity.userName,
    email: identity.email,
    company: identity.company,
    role: identity.company.role,
    isManager: isManagerRole(identity.company.role),
    isAdmin: isAdminRole(identity.company.role),
    db,
    engine: eng,
    repos,
    settings,
    saveSettings,
    details,
    saveDetails,
    active,
    terminals,
    multiTerminal: terminals.length > 1,
    terminal: terminals.length > 1 ? terminal : 'all',
    setTerminal,
    sync,
    syncStatus,
    signOut: onSignOut,
    switchCompany: onSwitchCompany,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Autosave hook: debounced save with flush on pagehide/hidden/app-inactive. */
export function useAutosaver(delay = 900): Autosaver {
  const ref = useRef<Autosaver | null>(null);
  if (!ref.current) ref.current = new Autosaver(delay);
  useEffect(() => {
    const a = ref.current!;
    const flush = () => void a.flush();
    const onVis = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVis);
    let remove: (() => void) | null = null;
    void import('@capacitor/app')
      .then(({ App }) => App.addListener('appStateChange', (s) => { if (!s.isActive) flush(); }))
      .then((h) => { remove = () => void h.remove(); })
      .catch(() => {});
    return () => {
      flush();
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVis);
      remove?.();
    };
  }, []);
  return ref.current;
}
